(() => {
  'use strict';

  if (String(document.documentElement.dataset.ofkMarket || '').toUpperCase() !== 'AU') return;

  const installKey = '__ofkAuVariantGuardV2Installed';
  if (window[installKey]) return;
  window[installKey] = true;

  const blockedVariantIds = new Set(['46140967354504', '46140967420040', '46140967682184']);
  const source = 'ofk-au-variant-guard-v2';
  const noticeKey = 'ofk-au-cart-guard-notice-v2';
  const acceleratedCheckoutSelector =
    'shopify-accelerated-checkout,shopify-accelerated-checkout-cart';
  const root = window.Shopify?.routes?.root || '/';
  const originalFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;
  const checkoutReplayForms = new WeakSet();
  const requestTimeoutMs = 8000;
  let cartInspection;
  let scheduledInspection;
  let cartState = 'unknown';
  let cartEpoch = 0;
  let checkoutIntent;
  let activePage = true;
  let writesInFlight = 0;

  try {
    const style = document.createElement('style');
    style.id = 'ofk-au-disable-accelerated-checkout';
    style.textContent = 'html[data-ofk-market="AU"] shopify-accelerated-checkout,html[data-ofk-market="AU"] shopify-accelerated-checkout-cart{display:none!important;}';
    (document.head || document.documentElement).appendChild(style);
  } catch (_) { /* The capture guard still rejects accelerated checkout. */ }

  const variantId = (value) => String(value || '').replace(/\D/g, '');
  const isBlockedVariant = (value) => blockedVariantIds.has(variantId(value));
  const withJsonSuffix = (value) => {
    const route = String(value || '');
    return route.endsWith('.js') || route.endsWith('.json') ? route : `${route}.js`;
  };
  const cartRoute = (() => {
    try {
      const url = new URL(window.routes?.cart_url || `${root}cart`, window.location.origin);
      if (url.origin !== window.location.origin || url.username || url.password || url.hash ||
          !/^\/en-au\/cart\/?$/.test(url.pathname)) return null;
      url.search = '';
      url.pathname = url.pathname.replace(/\/$/, '');
      return url;
    } catch (_) { return null; }
  })();
  const cartJsonUrl = cartRoute ? withJsonSuffix(cartRoute.href) : null;
  const marketAU = () => String(document.documentElement.dataset.ofkMarket || '').toUpperCase() === 'AU';
  const currentAU = () =>
    activePage && marketAU() &&
    (!window.Shopify?.country || String(window.Shopify.country).toUpperCase() === 'AU');
  const contextSnapshot = () => JSON.stringify([
    document.documentElement.dataset.ofkMarket, window.Shopify?.country || '',
    window.Shopify?.routes?.root || '', window.routes?.cart_url || '', window.location.pathname,
  ]);
  const isCartMutationRequest = (input, init) => {
    try {
      const rawUrl = typeof input === 'string' || input instanceof URL ? input : input?.url;
      const url = new URL(rawUrl, window.location.origin);
      const method = String(init?.method || input?.method || 'GET').toUpperCase();
      // Known measurement-only updates cannot change line availability. Do not let hung metadata block commerce.
      if (url.origin === window.location.origin && /\/cart\/update(?:\.js|\.json)?\/?$/.test(url.pathname) &&
          method === 'POST' && typeof init?.body === 'string') {
        try {
          const body = JSON.parse(init.body);
          if (body && typeof body === 'object' && !Array.isArray(body) && Object.keys(body).length === 1 &&
              Object.prototype.hasOwnProperty.call(body, 'attributes') && body.attributes &&
              typeof body.attributes === 'object' && !Array.isArray(body.attributes)) return false;
        } catch (_) { /* Unrecognized writes remain commercial mutations. */ }
      }
      return method !== 'GET' && method !== 'HEAD' && url.origin === window.location.origin &&
        /\/cart\/(?:add|change|update|clear)(?:\.js|\.json)?\/?$/.test(url.pathname);
    } catch (_) { return false; }
  };

  const availabilityMessage = () =>
    'This size is not available for delivery to Australia. Australia currently offers HIMARI 210 × 100 cm and SORA 200 × 100 cm.';

  const reviewMessage = () =>
    'Some sizes in your cart are not available for delivery to Australia. Review the items below and remove those sizes yourself in your Shopify cart. No items have been removed by this check. After reviewing your cart, click Checkout again.';
  const failureMessage = () =>
    'We could not verify your cart for Australia. No items have been removed by this check. Please review your Shopify cart or check again before checkout.';

  const acceleratedCheckoutMessage = () =>
    'Accelerated checkout is unavailable in Australia. Please add the item to your cart and use the standard checkout.';

  const showNotice = (message, isError = false, blocked = [], offerReview = false) => {
    try {
      if (!message || !document.body || !activePage || !marketAU()) return;
      let notice = document.getElementById('ofk-au-cart-guard-notice');
      if (!notice) {
        notice = document.createElement('div');
        notice.id = 'ofk-au-cart-guard-notice';
        notice.className = 'ofk-au-cart-guard-notice';
        notice.style.cssText =
          'display:block;box-sizing:border-box;width:calc(100% - 2rem);margin:1rem auto;padding:1rem;max-width:72rem;text-align:center;border:1px solid #121212;background:#fff;color:#121212;';
        notice.setAttribute('aria-atomic', 'true');
        document.body.prepend(notice);
      }
      notice.hidden = false;
      notice.setAttribute('role', isError ? 'alert' : 'status');
      notice.setAttribute('aria-live', isError ? 'assertive' : 'polite');
      notice.textContent = message;
      // A clean cart read cannot revoke a product-size or checkout-method restriction.
      notice.dataset.ofkAuNoticeKind =
        message === reviewMessage() || message === failureMessage() ||
        message === 'Your cart check is complete. Click Checkout when you are ready.' ? 'cart' : 'choice';
      if (blocked.length) {
        const list = document.createElement('ul');
        for (const item of blocked) {
          const row = document.createElement('li');
          row.textContent = `${item.title} — quantity ${item.quantity}`;
          list.appendChild(row);
        }
        notice.appendChild(list);
      }
      if (offerReview) {
        if (cartRoute && currentAU()) {
          const link = document.createElement('a');
          const url = new URL(cartRoute.href);
          url.searchParams.set('view', 'ofk-native');
          link.href = url.href;
          link.textContent = 'Review my Shopify cart and remove unavailable sizes';
          link.setAttribute('data-ofk-au-review-cart', '');
          notice.appendChild(link);
        }
        const check = document.createElement('button');
        check.type = 'button';
        check.textContent = 'Check my cart again';
        check.setAttribute('data-ofk-au-recheck', '');
        check.addEventListener('click', () => {
          markCartMayHaveChanged();
          inspectCart().catch(() => {});
        });
        notice.appendChild(check);
      }
    } catch (_) { /* A failed notice must never authorize checkout or a cart mutation. */ }
  };

  const rememberNotice = (message) => {
    if (!message) return;
    try {
      window.sessionStorage.setItem(noticeKey, message);
    } catch (error) {
      return;
    }
  };

  const restoreNotice = () => {
    try {
      const message = window.sessionStorage.getItem(noticeKey);
      if (!message) return;
      window.sessionStorage.removeItem(noticeKey);
      if (message === availabilityMessage()) showNotice(message);
    } catch (error) {
      return;
    }
  };

  const hasBlockedCartDom = () => {
    for (const id of blockedVariantIds) {
      const selector = [
        `cart-items [data-quantity-variant-id="${id}"]`,
        `cart-drawer [data-quantity-variant-id="${id}"]`,
        `cart-notification [data-quantity-variant-id="${id}"]`,
        `cart-notification a[href*="variant=${id}"]`,
      ].join(',');
      if (document.querySelector(selector)) return true;
    }
    return false;
  };

  const invalidateCheckout = () => {
    if (checkoutIntent) checkoutIntent.invalid = true;
    checkoutIntent = undefined;
  };
  const cancelInspection = () => {
    if (cartInspection) {
      cartInspection.cancelled = true;
      cartInspection.cancel?.();
      cartInspection = undefined;
    }
  };
  const markCartMayHaveChanged = () => {
    cartEpoch += 1;
    if (cartState !== 'dirty') cartState = 'unknown';
    invalidateCheckout();
    cancelInspection();
  };

  const requestJson = (token) => new Promise((resolve, reject) => {
    let done = false;
    let controller;
    try { if (typeof AbortController !== 'undefined') controller = new AbortController(); } catch (_) {}
    const finish = (callback, value) => {
      if (done) return;
      done = true;
      window.clearTimeout(timer);
      token.cancel = null;
      callback(value);
    };
    const abort = () => { try { controller?.abort(); } catch (_) {} };
    token.cancel = () => { finish(reject, new Error('AU_CART_READ_STALE')); abort(); };
    const timer = window.setTimeout(() => {
      finish(reject, new Error('AU_CART_READ_TIMEOUT'));
      abort();
    }, requestTimeoutMs);
    Promise.resolve().then(() => {
      if (token.cancelled || !currentAU() || !cartJsonUrl || !originalFetch) throw new Error('AU_CART_READ_CONTEXT');
      return originalFetch(cartJsonUrl, {
        method: 'GET', credentials: 'same-origin', cache: 'no-store',
        headers: { Accept: 'application/json' }, ...(controller ? { signal: controller.signal } : {}),
      });
    }).then(async (response) => {
      if (!response.ok) throw new Error('AU_CART_READ_FAILED');
      const cart = await response.json();
      finish(resolve, cart);
    }).catch((error) => finish(reject, error));
  });

  const cartSnapshot = (cart) => {
    if (!cart || !Array.isArray(cart.items) || cart.currency !== 'AUD' ||
        !Number.isSafeInteger(cart.item_count) || cart.item_count < 0) throw new Error('AU_CART_SCHEMA');
    const keys = new Set();
    let count = 0;
    const items = cart.items.map((item) => {
      if (!item || typeof item.key !== 'string' || !item.key || item.key.length > 512 || keys.has(item.key) ||
          !/^\d+$/.test(String(item.variant_id)) || !Number.isSafeInteger(item.quantity) || item.quantity <= 0) {
        throw new Error('AU_CART_ITEM_SCHEMA');
      }
      keys.add(item.key);
      count += item.quantity;
      if (!Number.isSafeInteger(count)) throw new Error('AU_CART_QUANTITY_SCHEMA');
      return {
        key: item.key, variant: String(item.variant_id), quantity: item.quantity,
        title: String(item.product_title || item.title || 'Unavailable size').slice(0, 160) +
          (item.variant_title ? ` (${String(item.variant_title).slice(0, 120)})` : ''),
      };
    });
    if (count !== cart.item_count) throw new Error('AU_CART_COUNT_SCHEMA');
    return Object.freeze({
      market: 'AU', currency: 'AUD',
      fingerprint: JSON.stringify(items.map((item) => [item.key, item.variant, item.quantity])),
      blocked: items.filter((item) => isBlockedVariant(item.variant)),
    });
  };

  const inspectCart = () => {
    window.clearTimeout(scheduledInspection);
    scheduledInspection = undefined;
    if (cartInspection) return cartInspection.promise;
    if (!currentAU() || !cartJsonUrl || writesInFlight) {
      cartState = 'unknown';
      showNotice(failureMessage(), true, [], true);
      return Promise.resolve({ clean: false, unknown: true });
    }
    const token = { epoch: cartEpoch, context: contextSnapshot(), cancelled: false };
    cartInspection = token;
    token.promise = requestJson(token).then((cart) => {
      if (token.cancelled || token.epoch !== cartEpoch || token.context !== contextSnapshot() || !currentAU()) {
        return { clean: false, stale: true };
      }
      const snapshot = cartSnapshot(cart);
      cartState = snapshot.blocked.length ? 'dirty' : 'clean';
      if (snapshot.blocked.length) showNotice(reviewMessage(), true, snapshot.blocked, true);
      else {
        const notice = document.getElementById('ofk-au-cart-guard-notice');
        if (notice?.dataset.ofkAuNoticeKind === 'cart') showNotice('Your cart check is complete. Click Checkout when you are ready.');
      }
      return { clean: cartState === 'clean', snapshot, epoch: token.epoch, context: token.context };
    }).catch(() => {
      if (!token.cancelled && token.epoch === cartEpoch && currentAU()) {
        cartState = hasBlockedCartDom() ? 'dirty' : 'unknown';
        showNotice(failureMessage(), true, [], true);
      }
      return { clean: false, unknown: true, stale: token.cancelled };
    }).finally(() => { if (cartInspection === token) cartInspection = undefined; });
    return token.promise;
  };

  const scheduleInspection = () => {
    window.clearTimeout(scheduledInspection);
    if (!currentAU()) return;
    scheduledInspection = window.setTimeout(() => {
      scheduledInspection = undefined;
      inspectCart().catch(() => {});
    }, 80);
  };

  const formSnapshot = (form, submitter) => JSON.stringify({
    action: form.action, method: form.method, target: form.target,
    enctype: form.enctype, noValidate: form.noValidate,
    submitter: submitter ? [submitter.name, submitter.value, submitter.disabled, submitter.type,
      submitter.getAttribute('formaction'), submitter.getAttribute('formmethod'),
      submitter.getAttribute('formtarget'), submitter.getAttribute('formenctype'), submitter.formNoValidate] : null,
    fields: Array.from(new FormData(form).entries(), ([name, value]) =>
      [name, typeof value === 'string' ? value : [value.name, value.size, value.type, value.lastModified]]),
  });
  const validCheckoutForm = (form, submitter) => {
    try {
      const action = new URL(submitter?.getAttribute('formaction') || form.action, window.location.origin);
      const method = String(submitter?.getAttribute('formmethod') || form.method).toLowerCase();
      const target = submitter?.getAttribute('formtarget') || form.target;
      const cartPath = cartRoute?.pathname;
      const checkoutPath = cartPath?.replace(/cart$/, 'checkout');
      return form.isConnected && (!submitter || (submitter.isConnected && submitter.form === form &&
        !submitter.disabled && submitter.type !== 'image')) && method === 'post' && (!target || target === '_self') &&
        action.origin === window.location.origin && !action.username && !action.password && !action.hash &&
        [cartPath, checkoutPath, '/checkout'].includes(action.pathname);
    } catch (_) { return false; }
  };

  const replayCheckout = (form, submitter) => {
    if (typeof form.requestSubmit !== 'function') throw new Error('AU_CHECKOUT_REPLAY_UNSUPPORTED');
    checkoutReplayForms.add(form);
    try {
      form.requestSubmit(submitter || undefined);
    } finally {
      checkoutReplayForms.delete(form);
    }
  };

  if (originalFetch) window.fetch = (...args) => {
    if (!marketAU()) return originalFetch(...args);
    if (!isCartMutationRequest(args[0], args[1])) return originalFetch(...args);
    markCartMayHaveChanged();
    writesInFlight += 1;
    let pending;
    try { pending = originalFetch(...args); }
    catch (error) {
      writesInFlight -= 1;
      markCartMayHaveChanged();
      scheduleInspection();
      throw error;
    }
    return Promise.resolve(pending).then((response) => {
      writesInFlight -= 1;
      markCartMayHaveChanged();
      scheduleInspection();
      return response;
    }, (error) => {
      writesInFlight -= 1;
      markCartMayHaveChanged();
      scheduleInspection();
      throw error;
    });
  };

  // Capture before document-level attribution link holds, whose continuation uses location.assign.
  window.addEventListener('click', (event) => {
    if (!marketAU()) return;
    const path = typeof event.composedPath === 'function' ? event.composedPath() : [event.target];
    const accelerated = path.some((node) => node?.matches?.(acceleratedCheckoutSelector));
    const link = event.target?.closest?.('a[href]');
    let checkoutLink = false;
    try {
      const url = link && new URL(link.href, window.location.origin);
      checkoutLink = !!url && url.origin === window.location.origin &&
        /^\/(?:[a-z]{2}(?:-[a-z]{2})?\/)?checkout\/?$/.test(url.pathname);
    } catch (_) {}
    if (!accelerated && !checkoutLink) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    markCartMayHaveChanged();
    showNotice(accelerated ? acceleratedCheckoutMessage() :
      'Use standard Checkout in your Australia Shopify cart after reviewing its items.', true, [], true);
  }, true);

  document.addEventListener('submit', async (event) => {
    if (!marketAU()) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (checkoutReplayForms.has(form)) {
      checkoutReplayForms.delete(form);
      return;
    }
    if (form.action.includes('/cart/add')) {
      const selectedVariant = variantId(form.querySelector('[name="id"]')?.value);
      if (!isBlockedVariant(selectedVariant)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showNotice(availabilityMessage(), true, [], true);
      return;
    }
    const isCheckout = form.action.includes('/checkout') || String(event.submitter?.name || '') === 'checkout';
    if (!isCheckout || event.defaultPrevented) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const submitter = event.submitter;
    if (checkoutIntent && !checkoutIntent.invalid && checkoutIntent.form === form && checkoutIntent.submitter === submitter) return;
    invalidateCheckout();
    cancelInspection();
    if (!currentAU() || !validCheckoutForm(form, submitter) || writesInFlight) {
      showNotice(failureMessage(), true, [], true);
      return;
    }
    const intent = { form, submitter, epoch: cartEpoch, context: contextSnapshot(), invalid: false };
    checkoutIntent = intent;
    try {
      intent.formSnapshot = formSnapshot(form, submitter);
      const result = await inspectCart();
      if (checkoutIntent !== intent || intent.invalid || !currentAU() || intent.epoch !== cartEpoch ||
          intent.context !== contextSnapshot() || !result.clean || result.epoch !== intent.epoch ||
          !validCheckoutForm(form, submitter) || intent.formSnapshot !== formSnapshot(form, submitter)) return;
      // Only this current customer submit may replay. Inspection, removal and lifecycle hooks never replay.
      invalidateCheckout();
      replayCheckout(form, submitter);
    } catch (_) {
      if (checkoutIntent === intent && currentAU()) showNotice(failureMessage(), true, [], true);
    } finally { if (checkoutIntent === intent) checkoutIntent = undefined; }
  }, true);

  const onReady = () => {
    restoreNotice();
    markCartMayHaveChanged();
    scheduleInspection();
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === source) return;
        markCartMayHaveChanged();
        scheduleInspection();
      });
    }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', onReady, { once: true });
  else onReady();

  window.addEventListener('pagehide', () => {
    activePage = false;
    markCartMayHaveChanged();
    window.clearTimeout(scheduledInspection);
  });
  window.addEventListener('pageshow', (event) => {
    activePage = true;
    if (!event.persisted) return;
    markCartMayHaveChanged();
    scheduleInspection();
  });
  window.addEventListener('focus', () => { markCartMayHaveChanged(); scheduleInspection(); });
  document.addEventListener('visibilitychange', () => {
    markCartMayHaveChanged();
    if (document.visibilityState === 'visible') scheduleInspection();
  });
  document.addEventListener('change', (event) => {
    if (event.target?.name !== 'country_code') return;
    markCartMayHaveChanged();
    scheduleInspection();
  }, true);
  if (typeof MutationObserver !== 'undefined') new MutationObserver(() => {
    const notice = document.getElementById('ofk-au-cart-guard-notice');
    if (notice && !marketAU()) notice.hidden = true;
    markCartMayHaveChanged();
    scheduleInspection();
  }).observe(document.documentElement, { attributes: true, attributeFilter: ['data-ofk-market'] });

  // Keep the existing unsupported-variant URL recovery; hooks are installed even if navigation is blocked.
  try {
    const requestedUrl = new URL(window.location.href);
    if (isBlockedVariant(variantId(requestedUrl.searchParams.get('variant')))) {
      rememberNotice(availabilityMessage());
      requestedUrl.searchParams.delete('variant');
      window.location.replace(requestedUrl.toString());
    }
  } catch (_) { showNotice(availabilityMessage(), true, [], true); }
})();
